import React, { useState, useEffect, useRef } from 'react';
import { ShoppingBag, Package, Stethoscope } from 'lucide-react';
import { Card } from './ui/card';

const StatsSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const sectionRef = useRef(null);

  const stats = [
    {
      icon: ShoppingBag,
      number: 1000,
      displayNumber: '1,000',
      suffix: '+',
      label: 'Brands',
    },
    {
      icon: Package,
      number: 50000,
      displayNumber: '50,000',
      suffix: '+',
      label: 'Products',
    },
    {
      icon: Stethoscope,
      number: 70,
      displayNumber: '70',
      suffix: '+',
      label: 'Dermatologists',
    },
  ];

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Intersection Observer for scroll detection
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Counter component for mobile
  const Counter = ({ target, displayNumber, suffix }) => {
    const [count, setCount] = useState(0);

    useEffect(() => {
      if (!isVisible || !isMobile) return;

      const duration = 2000; // 2 seconds
      const steps = 60;
      const increment = target / steps;
      let current = 0;

      const timer = setInterval(() => {
        current += increment;
        if (current >= target) {
          setCount(target);
          clearInterval(timer);
        } else {
          setCount(Math.floor(current));
        }
      }, duration / steps);

      return () => clearInterval(timer);
    }, [isVisible, isMobile, target]);

    // Format number with commas
    const formatNumber = (num) => {
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    };

    if (!isMobile) {
      return (
        <>
          {displayNumber}
          <span className="text-brand-500">{suffix}</span>
        </>
      );
    }

    return (
      <>
        {formatNumber(count)}
        <span className="text-brand-500">{suffix}</span>
      </>
    );
  };

  return (
    <section id="why-us" className="py-16 bg-white" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            On-demand Beauty and Skincare
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card
                key={index}
                className="p-8 text-center hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border-2 hover:border-brand-500 bg-gradient-to-br from-white to-brand-50"
              >
                <div className="flex justify-center mb-4">
                  <div className="p-4 bg-brand-100 rounded-full">
                    <Icon className="w-8 h-8 text-brand-600" />
                  </div>
                </div>
                <div className="text-4xl md:text-5xl font-bold text-gray-900 mb-2">
                  <Counter 
                    target={stat.number} 
                    displayNumber={stat.displayNumber}
                    suffix={stat.suffix} 
                  />
                </div>
                <p className="text-gray-600 text-lg">{stat.label}</p>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default StatsSection;
